import type { GraphQLError } from 'graphql';

import { ContentRendererExternalQuery } from '@confluence/content-body/entry-points/ContentRendererExternalQuery.graphql.ts';
import type {
	ContentRendererExternalQueryType,
	ContentRendererExternalQueryVariablesType,
} from '@confluence/content-body/entry-points/ContentRendererExternalQuery.ts';
import { preloadContentTitleWithByLineExternal } from '@confluence/content-header/entry-points/preloadContentTitleWithByLineExternal.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';

import SharedContentQuery from './__generated__/SharedContentQuery.graphql.ts';
import type {
	SharedContentQuery as SharedContentQueryType,
	SharedContentQueryVariables,
} from './__types__/SharedContentQuery.ts';

type PreloadContentTitleWithByLineExternalResult = Awaited<
	ReturnType<typeof preloadContentTitleWithByLineExternal>
>;

export const preloadExternalShare = async ({
	shareToken,
	contentId,
}: PreloadExternalShareArgs): Promise<
	| void
	| Error[]
	| (
			| void
			| {
					data: SharedContentQueryType | null;
					errors?: ReadonlyArray<GraphQLError>;
					hasErrors: boolean;
			  }
			| Error[]
			| {
					data: ContentRendererExternalQueryType | null;
					errors?: ReadonlyArray<GraphQLError>;
					hasErrors: boolean;
			  }
			| PreloadContentTitleWithByLineExternalResult
	  )[]
> => {
	const sharedContentResult = await query<SharedContentQueryType, SharedContentQueryVariables>({
		query: SharedContentQuery,
		variables: {
			shareToken,
			contentId,
		},
		context: {
			allowOnExternalPage: true,
		},
	});

	const singleContent = sharedContentResult?.data?.singleContent;
	const resolvedContentId = contentId ?? singleContent?.id;
	const isPage = singleContent?.type === 'page';

	if (!resolvedContentId || !isPage) {
		return waitForAllReturnWhenError([Promise.resolve(sharedContentResult)]);
	}

	return waitForAllReturnWhenError([
		Promise.resolve(sharedContentResult),
		query<ContentRendererExternalQueryType, ContentRendererExternalQueryVariablesType>({
			query: ContentRendererExternalQuery,
			variables: {
				contentId: resolvedContentId,
			},
			context: {
				allowOnExternalPage: true,
			},
		}),
		preloadContentTitleWithByLineExternal(resolvedContentId),
	]);
};

type PreloadExternalShareArgs = {
	shareToken: string;
	contentId?: string;
};
